import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoadingController } from 'ionic-angular';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable()

/**
 * Provider criado para possibilizar a doação para planos de contribuição
 *
 * @property baseUrl URL base do backend
 * @property planosAdicionados Planos que foram adicionados para realziar a contribuição
 * @property planosAdicionadosTotal Valor total dos planos adicionados
 * @property quantidadePlanosAdicionados Quantidade total de planos adicionados
 * @property pessoa Objeto com informações do contribuinte
 * @property formaPagamento Forma de pagamento selecionada para realizar a contribuição
 * @property passo Numero da etapa da contribuição que o usuario esta
 * @property tokenIugu Token da conta na iugu
 * @property dadosCartao Dados do cartao de credito
 * @property boletoUrl Quando pagamento for por boleto, grava-se as urls para impressão dele e da fatura.
 * @property respostaPagamento Guarda status final de pagamento
 *
 * @method formasPagamentoDisponiveis Retorna formas de pagamentos habilitadas para Instituição
 * @method limpaCarrinho Remove todos os planos adicionados no "carrinho"
 * @method getPlanos Retorna planos de contribuição cadastrados para Instituição
 * @method getMensagemAgradecimento Retorna mensagem dinamica de agradecimento pela contribuição
 *
 * @author Fernando Binasco <fernando.binasco@gmail.com>
 * @since 1.0
 * @date 21/11/2019
 */
export class ContribuaProvider {
  baseUrl = environment.API_URL;
  pagamentoUrl: string = '';
  planosAdicionados: any = [];
  planosAdicionadosTotal: number = 0;
  quantidadePlanosAdicionados: number = 0;
  pessoa: any = {};
  formaPagamento: string = '';
  passo: number = 0;
  tokenIugu: any = {};
  dadosCartao: any = {};
  idInstituicao: number = 0;
  respostaPagamento: any = [];
  formasPagamentoDisponiveis: any = [];
  observacao: string = '';

  constructor(
    public http: HttpClient,
    private loadingCtrl: LoadingController,
    public translate: TranslateService,
  ) {}

  /**
   * Retorna mensagem dinamica de agradecimento pela contribuição
   *
   * @param NENHUM
   *
   * @return String
   */
  public getMensagemAgradecimento() {
    return this.http.post<any>(this.baseUrl + '/contribua/get-mensagem-agradecimento', {}).map((retorno) => {
      if (retorno) {
        return retorno;
      } else {
        return false;
      }
    });
  }

  /**
   * Retorna planos de contribuição cadastrados para Instituição
   *
   * @param NENHUM
   * @return Object
   */
  public getFormasPagamentoDisponiveis() {
    return this.http.post<any>(this.baseUrl + '/contribua/formas-pagamento', {}).map((retorno) => {
      if (!retorno.error) {
        this.formasPagamentoDisponiveis = retorno;
        return retorno;
      } else {
        return false;
      }
    });
  }

  /**
   * Remove todos os planos adicionados no "carrinho"
   *
   * @param NENHUM
   * @return NENHUM
   */
  public limpaCarrinho() {
    this.planosAdicionados = [];
    this.planosAdicionadosTotal = 0;
    this.quantidadePlanosAdicionados = 0;
    this.respostaPagamento = false;
    this.formaPagamento = '';
  }

  /**
   * Retorna planos de contribuição cadastrados para Instituição
   *
   * @param destaque integer sendo 0 | 1
   * @return
   */
  public getPlanos(destaque) {
    return this.http
      .post<any>(this.baseUrl + '/contribua/planos-contribuicao', {
        destaque: destaque,
        idPessoa: this.pessoa.id,
      })
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }

  /**
   * Recebe token Iugu
   */
  public getTokenIugu() {
    return this.http.post<any>(this.baseUrl + '/contribua/get-token-iugu', {}).map((retorno) => {
      if (!retorno.error) {
        return retorno;
      } else {
        return false;
      }
    });
  }

  /**
   * Retorna dados bancarios para instituicao
   */
  public getContasBancarias() {
    let loader: any = this.loadingCtrl.create({
      content: this.translate.instant('Carregando'),
    });
    loader.present();
    return this.http
      .post<any>(this.baseUrl + '/contribua/contas-bancarias', {})
      .pipe(finalize(() => loader.dismiss()))
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }

  /**
   * Retorna os planos contratados de forma recorrente
   */
  public getContribuicoes() {
    let loader: any = this.loadingCtrl.create({
      content: this.translate.instant('Carregando'),
    });
    loader.present();
    return this.http
      .post<any>(this.baseUrl + '/contribua/lista-contribuicoes', {
        idPessoa: this.pessoa.id,
      })
      .pipe(finalize(() => loader.dismiss()))
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }

  /**
   * Realiza o ação de pagamento da contribuição via integração com Iugu
   */
  public pagamento(tokenIuguPagamento) {
    return this.http
      .post<any>(this.baseUrl + '/contribua/pagamento', {
        dadosCartao: this.dadosCartao,
        planosAdicionados: this.planosAdicionados,
        tokenIuguPagamento: tokenIuguPagamento,
        pessoa: this.pessoa,
        formaPagamento: this.formaPagamento,
        observacao: this.observacao,
      })
      .map((retorno) => {
        if (!retorno.error) {
          // quando boleto seta url para pagamento
          if (this.formaPagamento == 'boleto') {
            this.pagamentoUrl = retorno.boleto.urlPagamento;
          }
          // adiciona array com dados dos planos contratados
          this.respostaPagamento = retorno;
          return true;
        } else {
          return retorno;
        }
      });
  }

  /**
   * Realiza a ação de pagamento de uma contribuição via integração com Pagseguro
   */
  public pagamentoPagseguro() {
    let loader: any = this.loadingCtrl.create({
      content: this.translate.instant('Carregando'),
    });
    loader.present();
    return this.http
      .post<any>(this.baseUrl + '/contribua/pagamento-pagseguro', {
        planosAdicionados: this.planosAdicionados,
        pessoa: this.pessoa,
      })
      .pipe(finalize(() => loader.dismiss()))
      .map((retorno) => {
        if (!retorno.error) {
          this.pagamentoUrl = retorno.pagseguro;
          this.respostaPagamento = retorno;
          return true;
        } else {
          return retorno;
        }
      });
  }

  /**
   * Realiza a ação de pagamento de uma contribuição via integração com Paypal
   */
  public pagamentoPaypal() {
    let loader: any = this.loadingCtrl.create({
      content: this.translate.instant('Carregando'),
    });
    loader.present();
    return this.http
      .post<any>(this.baseUrl + '/contribua/pagamento-paypal', {
        planosAdicionados: this.planosAdicionados,
        pessoa: this.pessoa,
      })
      .pipe(finalize(() => loader.dismiss()))
      .map((retorno) => {
        if (!retorno.error) {
          this.pagamentoUrl = retorno.paypal;
          this.respostaPagamento = retorno;
          return true;
        } else {
          return retorno;
        }
      });
  }

  /**
   * Gera boleto com base na parcela
   */
  public geraBoletoParcela(parcela) {
    return this.http
      .post<any>(this.baseUrl + '/contribua/criar-boleto-parcela', {
        parcela: parcela,
        pessoa: this.pessoa,
      })
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }

  /**
   * Gera link pagseguro com base na parcela
   */
  public geraPagseguroParcela(parcela) {
    return this.http
      .post<any>(this.baseUrl + '/contribua/criar-pagseguro-parcela', {
        parcela: parcela,
        pessoa: this.pessoa,
      })
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }

  /**
   * Gera link paypal com base na parcela
   */
  public geraPaypalParcela(parcela) {
    return this.http
      .post<any>(this.baseUrl + '/contribua/criar-paypal-parcela', {
        parcela: parcela,
        pessoa: this.pessoa,
      })
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }

  /**
   * Cancela uma contribuição
   */
  public cancelaContribuicao(idPlanoPessoa) {
    let loader: any = this.loadingCtrl.create({
      content: this.translate.instant('Carregando'),
    });
    loader.present();
    return this.http
      .post<any>(this.baseUrl + '/contribua/cancelar-contribuicao', {
        idPlanoPessoa: idPlanoPessoa,
      })
      .pipe(finalize(() => loader.dismiss()))
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }

  /**
   * Carrega historico de contribuições realizadas com recorrencia
   */
  public listaPlanoContribuicaoPessoa(plano) {
    let loader: any = this.loadingCtrl.create({
      content: this.translate.instant('Carregando'),
    });
    loader.present();
    return this.http
      .post<any>(this.baseUrl + '/contribua/lista-plano-contribuicao-pessoa', {
        idPlano: plano.idPlano,
      })
      .pipe(finalize(() => loader.dismiss()))
      .map((retorno) => {
        if (!retorno.error) {
          return retorno;
        } else {
          return false;
        }
      });
  }
}



// WEBPACK FOOTER //
// ./src/providers/contribua/contribua.ts